import { FieldArray, getIn, useFormikContext } from 'formik'
import { Fragment, ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from 'components/v2/Row'
import { EVehiclePlateType, ILicensePlatesFormValues } from 'features/Vehicles/types'
import { STATES } from 'utils/states'
import { Label } from 'components/v2/Label'
import { COUNTRY_CODES } from 'utils/countries'
import { SelectState } from 'features/Cities/components/SelectState'
import { Select } from 'components/v2/Select'
import FormLayout from 'components/v2/FormLayout'
import { Button } from 'components/v2/Button'
import { CancelIcon } from 'components/Icons/CancelIcon'

const VEHICLE_PLATE_TYPE_LIST = [
  EVehiclePlateType.EmployeeTransport,
  EVehiclePlateType.Federal,
  EVehiclePlateType.Load,
  EVehiclePlateType.Local,
  EVehiclePlateType.Other,
  EVehiclePlateType.Pass,
  EVehiclePlateType.PublicTransport,
  EVehiclePlateType.Test
]

const messages = defineMessages({
  platePlaceholder: {
    id: 'vehicles.form.plate.placeholder',
    defaultMessage: 'Enter the vehicle plate'
  },
  typePlaceholder: {
    id: 'vehicles.form.plate.type.placeholder',
    defaultMessage: 'Select type'
  }
})

export const LicensePlatesForm = (): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setFieldValue, handleBlur, touched, errors } =
    useFormikContext<ILicensePlatesFormValues>()

  return (
    <FieldArray
      name="licensePlates"
      render={(arrayHelpers): ReactElement => (
        <>
          <FormLayout.CardHeader>
            <Heading.H4>
              <FormattedMessage id="vehicles.form.licensePlates.title" defaultMessage="Plates" />
            </Heading.H4>
            <Button
              kind="secondary"
              type="button"
              onClick={(): void =>
                arrayHelpers.push({
                  type: '',
                  number: '',
                  state: null
                })
              }
            >
              <FormattedMessage id="vehicles.form.licensePlates.add" defaultMessage="+ Additional plate" />
            </Button>
          </FormLayout.CardHeader>
          {values.licensePlates.map((plate, index) => {
            const handleStateChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
              const stateName = value && STATES.find((state) => state.code === value)?.name

              if (stateName) {
                setFieldValue(`licensePlates.${index}.state`, {
                  code: value,
                  name: stateName
                })
              } else {
                setFieldValue(`licensePlates.${index}.state`, null)
              }
            }
            return (
              <Fragment key={index}>
                {index > 0 ? (
                  <>
                    <FormLayout.Divider />
                    <FormLayout.CardHeader>
                      <div />
                      <FormLayout.DeleteItemAction onClick={(): void => arrayHelpers.remove(index)}>
                        <CancelIcon />
                      </FormLayout.DeleteItemAction>
                    </FormLayout.CardHeader>
                  </>
                ) : null}
                <Row noMargin>
                  <Column>
                    <Select
                      id={`licensePlates.${index}.type`}
                      value={plate.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.licensePlates?.[index]?.type}
                      errors={getIn(errors, `licensePlates.${index}.type`)}
                      label={
                        <FormattedMessage id="vehicles.form.plates.type.label" defaultMessage="Type vehicle plate" />
                      }
                    >
                      <option value="">{intl.formatMessage(messages.typePlaceholder)}</option>
                      {VEHICLE_PLATE_TYPE_LIST.map((type): ReactElement => {
                        return (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        )
                      })}
                    </Select>
                  </Column>
                  <Column>
                    <Input
                      id={`licensePlates.${index}.number`}
                      value={plate.number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={intl.formatMessage(messages.platePlaceholder)}
                      label={<FormattedMessage id="vehicles.form.plates.number.label" defaultMessage="Vehicle plate" />}
                      touched={touched.licensePlates?.[index]?.number}
                      errors={getIn(errors, `licensePlates.${index}.number`)}
                    />
                  </Column>
                  <Column>
                    <Label optional>
                      <FormattedMessage id="dictionary.state" defaultMessage="State" />
                    </Label>
                    <SelectState
                      id={`licensePlates.${index}.state`}
                      value={plate.state?.code || ''}
                      onChange={handleStateChange}
                      onBlur={handleBlur}
                      country={COUNTRY_CODES.MEXICO}
                    />
                  </Column>
                </Row>
              </Fragment>
            )
          })}
        </>
      )}
    />
  )
}
